import React, { useCallback, useEffect, useRef, useState } from 'react'
import Alert from '../../../components/Alerts/Alert.jsx'
import { httpGet, httpPut } from '../../../store/services/testServiceClient.js'
import EditableTextCell from '../../../components/EditableTextCell.jsx'
import { Button, Empty, Modal } from 'antd'
import EditableTableCell from './EditableTableCell.jsx'
import UMToolTip from '../../../components/UMToolTip.jsx'

const Scenarios = ({ test, testPath, projectId, setTest }) => {
  const selectAllCheckbox = useRef()
  const scenarioTableBody = useRef()
  const alertbox = useRef()
  const csvfile = useRef()
  const [doesParametersContainTable, setDoesParametersContainTable] = useState(false)
  const [parameters, setParameters] = useState([])
  const [scenarios, setScenarios] = useState([])
  const [isLoadingScenarios, setIsLoadingScenarios] = useState(false)
  const [scenariosProgressText, setScenariosProgressText] = useState('')
  const [someScenariosSelected, setSomeScenariosSelected] = useState(false)
  const [canAddScenario, setCanAddScenario] = useState(true)
  const [selectedCellId, setSelectedCellId] = useState('')
  const [isImportModalVisible, setIsImportModalVisible] = useState(false)

  const showError = useCallback((message, e) => {
    alertbox.current?.showServerError(message, e)
    scrollToElement('alert_box')
  }, [])

  const populateScenarioArgumentVMs = useCallback(
    (_scenarios) => {
      _scenarios.forEach((s) => {
        s.isSelected = false
        s.argsVM = []
      })

      parameters.forEach((p) => {
        if (p.DataType === 'Table') {
          setDoesParametersContainTable(true)
        }

        _scenarios.forEach((s) => {
          const arg = s.Arguments.filter((a) => a.ParameterId === p.Id)
          let argValue = ''

          if (arg && arg[0] && arg[0].Value) {
            if (arg[0].Value.Text) {
              argValue = arg[0].Value.Text
            } else if (
              arg[0].Value.Headers &&
              Array.isArray(arg[0].Value.Headers)
            ) {
              argValue = arg[0].Value.Headers
            }
          }
          s.argsVM.push({ ParameterId: p.Id, Value: argValue })
        })
      })

      setScenarios(_scenarios)
    },
    [parameters]
  )

  const loadParameter = useCallback(async () => {
    try {
      showScenarioProgress('Loading datasets for your test...')
      setParameters((await httpGet(testPath)).data.Parameters)
    } catch (e) {
      showError('Error loading parameters', e)
    } finally {
      hideScenarioProgress()
    }
  }, [showError])

  const loadScenarios = useCallback(async () => {
    showScenarioProgress('Loading datasets for your test...')
    try {
      const _scenarios = (await httpGet(`${testPath}/scenarios`)).data
      setScenarios(_scenarios)
      populateScenarioArgumentVMs(_scenarios)
      setTest((prev) => {
        return { ...prev, HasScenarios: _scenarios.length > 0 }
      })
    } catch (e) {
      showError('Error loading datasets.', e)
    } finally {
      hideScenarioProgress()
    }
  }, [populateScenarioArgumentVMs, showError, testPath])

  const showImportDialog = async () => {
    if (csvfile.current) csvfile.current.value = ''
    setIsImportModalVisible(true)
  }

  const getParameterFromCellId = (cellId) => {
    const paramId = cellId.split('-')[1]
    const parameter = parameters.find((param) => param.Id === paramId)
    return parameter
  }

  const importCsv = async () => {
    setIsImportModalVisible(false)
    showScenarioProgress('Importing datasets...')
    const csv = csvfile.current.files[0]
    if (!csv.size > 0) {
      showError('The csv file should have valid content.')
      hideScenarioProgress()
      return
    } else if (csv.name.split('.').pop().toLowerCase() !== 'csv') {
      showError('Select a .csv file.')
      hideScenarioProgress()
      return
    }
    try {
      const headers = { headers: { 'Content-Type': 'text/csv' } }
      await httpPut(`${testPath}/importScenarios`, csv, headers)
      await loadScenarios()
    } catch (e) {
      showError(`Error importing datasets from file: '${csv.name}'.`, e)
    } finally {
      hideScenarioProgress()
    }
  }

  const deleteScenarios = async () => {
    try {
      if (areAllScenariosSelected()) {
        await deleteScenarioSet()
      } else {
        await deleteSelectedScenarios()
      }
      await loadScenarios()
      setSomeScenariosSelected(false)
    } catch (e) {
      showError('Error deleting datasets.', e)
    } finally {
      hideScenarioProgress()
    }
  }

  const deleteSelectedScenarios = async () => {
    for (let i = 0; i < scenarios.length; i++) {
      if (scenarios[i].isSelected) {
        showScenarioProgress(`Deleting dataset '${scenarios[i].Name}'...`)
        await httpPut(`${testPath}/deleteScenario`, {
          ScenarioId: scenarios[i].Id
        })
      }
    }
  }

  const deleteScenarioSet = async () => {
    showScenarioProgress('Deleting datasets...')
    await httpPut(`${testPath}/deleteScenarioSet`)
  }

  const addScenario = async () => {
    try {
      setCanAddScenario(false)
      const args = []
      parameters.forEach((p) =>
        args.push({ ParameterId: p.Id, Value: p.DefaultValue })
      )
      const body = { Name: getUniqueName(), Arguments: args }
      showScenarioProgress(`Adding dataset '${body.Name}'...`)
      await httpPut(`${testPath}/addScenario`, body)
      await loadScenarios()
      hideScenarioProgress()
      scrollToElement('footer')
    } catch (e) {
      hideScenarioProgress()
      showError('Error adding new dataset.', e)
    } finally {
      setCanAddScenario(true)
    }
  }

  const scrollToElement = (elementId) => {
    const ele = document.getElementById(elementId)
    ele?.scrollIntoView()
  }

  const getUniqueName = () => {
    const namePrefix = 'Unnamed Dataset'
    let newName = `${namePrefix}`
    let index = 1
    // eslint-disable-next-line
    while (scenarios.some((s) => s.Name === newName)) {
      newName = `${namePrefix} ${index++}`
    }
    return newName
  }

  const showScenarioProgress = (text) => {
    alertbox.current?.hideAlert()
    setIsLoadingScenarios(true)
    setScenariosProgressText(text)
  }

  const hideScenarioProgress = () => {
    setIsLoadingScenarios(false)
  }

  const selectAll = () => {
    const checkbox = selectAllCheckbox
    const _scenarios = scenarios?.map((s) => ({
      ...s,
      isSelected: checkbox.current.checked
    }))
    setScenarios(_scenarios)
    const scenarioChecks =
      scenarioTableBody.current.getElementsByTagName('input')
    for (const sc of scenarioChecks) {
      sc.checked = checkbox.current.checked
    }
    setSomeScenariosSelected(checkbox.current.checked)
  }

  const enableDisableDeleteScenariosButton = (e, id) => {
    const _scenarios = scenarios?.map((scenario) =>
      scenario.Id === id
        ? (scenario = { ...scenario, isSelected: e.target.checked })
        : scenario
    )
    setScenarios(_scenarios)
    setSomeScenariosSelected(_scenarios.some((s) => s.isSelected === true))
    selectAllCheckbox.current.checked = _scenarios.every(
      (s) => s.isSelected === true
    )
  }

  const areAllScenariosSelected = () => {
    return scenarios.every((s) => s.isSelected === true)
  }

  const selectCell = (id) => {
    setSelectedCellId(id)
  }

  const getCellId = (scenarioId, columnId) => {
    return `${scenarioId}-${columnId}`
  }

  const getScenarioNameCellId = (scenarioId) => {
    return `${scenarioId}-name`
  }

  const getScenarioRowId = (scenarioId) => {
    return `${scenarioId}-row`
  }

  const updateParameterValue = async (newValue) => {
    alertbox.current?.hideAlert()
    const index = parseInt(selectedCellId.split('-')[0])
    const parameter = getParameterFromCellId(selectedCellId)
    const scenario = scenarios.find((s) => s.Id === index)
    try {
      await httpPut(`${testPath}/updateScenario`, {
        Name: null,
        ScenarioId: scenario.Id,
        Values: [
          { ParameterId: selectedCellId.split('-')[1], Value: newValue }
        ]
      })
      setScenarios(
        scenarios?.map((s) =>
          s.Id === index
            ? (s = {
                ...s,
                argsVM: s.argsVM?.map((a) =>
                  a.ParameterId === parameter.Id
                    ? (a = { ...a, Value: newValue })
                    : a
                )
              })
            : s
        )
      )
      return newValue
    } catch (e) {
      showError(
        `Error saving argument value '${newValue}' for dataset: ${scenario.Name}.`,
        e
      )
      return undefined
    }
  }

  const updateScenarioName = async (newName) => {
    alertbox.current?.hideAlert()
    const index = parseInt(selectedCellId.split('-')[0])
    const scenario = scenarios.find((s) => s.Id === index)
    try {
      await httpPut(`${testPath}/updateScenario`, {
        Name: newName,
        ScenarioId: scenario.Id,
        Values: []
      })
      setScenarios(
        scenarios?.map((s) =>
          s.Name === scenario.Name ? (s = { ...s, Name: newName }) : s
        )
      )
      return newName
    } catch (e) {
      showError(`Error renaming dataset: ${scenario.Name} to '${newName}'.`, e)
      return undefined
    }
  }

  useEffect(() => {
    loadParameter()
  }, [loadParameter])

  useEffect(() => {
    if (parameters.length) {
      loadScenarios()
    }
  }, [parameters, loadScenarios])

  useEffect(() => {
    if (!scenarios.length > 0) {
      setTest((prev) => {
        return { ...prev, HasScenarios: false }
      })
    }
  }, [scenarios])

  return (
    <>
      <Alert showCloseButton ref={alertbox} />
      <div className='px-0 w-full shadow-sm pb-2'>
        <div className='px-4 h-12 bg-transparentColor font-bold text-md w-full flex justify-between items-center'>
          Test Datasets
        </div>
        <div className='p-4' id='scenariosPanel'>
          <div className='truncate'>
            <p className='pb-2 whitespace-normal'>
              Use <em>datasets</em> to run your test with different sets of
              input data. These enable you to use the same test logic for
              multiple test cases, or to perform the same test in different
              environments.
            </p>
            <div
              className='w-full grid grid-cols-1 gap-y-2 sm:grid-cols-4 sm:gap-x-2'
              disabled={isLoadingScenarios}
            >
              <Button
                id='addScenarioBtn'
                type='primary'
                disabled={!canAddScenario || isLoadingScenarios}
                loading={!canAddScenario}
                onClick={addScenario}
              >
                <span> Add Dataset </span>
              </Button>
              <Button
                id='deleteScenariosBtn'
                type='primary'
                disabled={!someScenariosSelected || isLoadingScenarios}
                loading={isLoadingScenarios}
                className='bg-red-500 border-red-500 hover:border-red-300 hover:bg-red-300'
                onClick={deleteScenarios}
              >
                Delete Datasets
              </Button>
              <Button
                id='importScenariosBtn'
                type='primary'
                disabled={isLoadingScenarios}
                onClick={showImportDialog}
                className='sm:col-start-4 flex justify-center items-center'
              >
                <i className='fa fa-upload mr-3' aria-hidden='true' />
                Import Datasets
              </Button>
            </div>
            {isLoadingScenarios ? (
              <div className='mt-3'>
                <p>{scenariosProgressText}</p>
              </div>
            ) : test.HasScenarios ? (
              <div className='mt-3 flex flex-col'>
                <div className='-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
                  <div className='py-2 align-middle inline-block sm:px-6 lg:px-8'>
                    <div className='shadow overflow-hidden border-b border-gray-200 sm:rounded-sm'>
                      <table className='dataset-table divide-y divide-gray-200 border-solid border-1 border-gray-100'>
                        <thead className='bg-gray-50'>
                          <tr>
                            <th
                              scope='col'
                              className='px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-solid border-1 border-gray-100'
                            >
                              <input
                                id='selectAllScenariosCheckbox'
                                type='checkbox'
                                width='1%'
                                onClick={selectAll}
                                value={areAllScenariosSelected}
                                ref={selectAllCheckbox}
                              />
                            </th>
                            <th
                              scope='col'
                              className='px-6 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-solid border-1 border-gray-100'
                            >
                              Dataset Name
                            </th>
                            {parameters?.map((param) => (
                              <th
                                key={param.Id}
                                scope='col'
                                className='truncate px-6 py-0 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-solid border-1 border-gray-100'
                              >
                                <UMToolTip>{param.Name}</UMToolTip>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody
                          ref={scenarioTableBody}
                          className='bg-white divide-y divide-gray-200 border-solid border-1 border-gray-100'
                        >
                          {scenarios?.map((scenario) => (
                            <tr
                              key={scenario.Id}
                              id={getScenarioRowId(scenario.Id)}
                            >
                              <td className='px-6 py-0 whitespace-nowrap border-solid border-1 border-gray-100'>
                                <input
                                  type='checkbox'
                                  value={scenario.isSelected}
                                  onChange={(e) =>
                                    enableDisableDeleteScenariosButton(
                                      e,
                                      scenario.Id
                                    )}
                                />
                              </td>
                              <td className='whitespace-nowrap border-solid border-1 border-gray-100 hover:bg-gray-100 w-full'>
                                <EditableTextCell
                                  value={scenario.Name}
                                  id={getScenarioNameCellId(scenario.Id)}
                                  updateValue={updateScenarioName}
                                  selected={
                                    getScenarioNameCellId(scenario.Id) ===
                                    selectedCellId
                                  }
                                  setSelected={selectCell}
                                />
                              </td>
                              {scenario.argsVM?.map((arg, index) =>
                                scenario.Arguments[index].Value.ArgType ===
                                'Table' ? (
                                  <EditableTableCell
                                    arg={arg}
                                    cellId={getCellId(
                                      scenario.Id,
                                      arg.ParameterId
                                    )}
                                    projectId={projectId}
                                    scenarios={scenarios}
                                    showError={showError}
                                    scenario={scenario}
                                    testPath={testPath}
                                    scenarioIndex={index}
                                    testId={test.Id}
                                    getParameterFromCellId={
                                      getParameterFromCellId
                                    }
                                    loadScenarios={loadScenarios}
                                  />
                                    ) : (
                                      <td className='whitespace-nowrap border-solid border-1 border-gray-100 hover:bg-gray-100 w-full'>
                                        <EditableTextCell
                                          value={arg.Value}
                                          updateValue={updateParameterValue}
                                          id={getCellId(
                                            scenario.Id,
                                            arg.ParameterId
                                          )}
                                          selected={
                                        getCellId(
                                          scenario.Id,
                                          arg.ParameterId
                                        ) === selectedCellId
                                      }
                                          setSelected={selectCell}
                                        />
                                      </td>
                                    )
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <b>
                    Your test doesn't have any datasets. You can add or import
                    datasets.
                  </b>
                }
              />
            )}
          </div>
        </div>
      </div>

      <Modal
        visible={isImportModalVisible}
        destroyOnClose
        onCancel={() => setIsImportModalVisible(false)}
        footer={
          <>
            <Button type='primary' id='importBtn' onClick={importCsv}>
              Import
            </Button>
            <Button
              type='default'
              id='cancelBtn'
              onClick={() => setIsImportModalVisible(false)}
            >
              Cancel
            </Button>
          </>
        }
        title='Import datasets from a csv file'
      >
        {doesParametersContainTable && <p>Only text values will get assigned while importing datasets.</p>}
        <input type='file' ref={csvfile} accept='.csv' id='csvfileinput' />
      </Modal>
    </>
  )
}

export default Scenarios
